import React from "react";
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Tooltip,
  Alert,
} from "@material-ui/core";
import {
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
} from "@material-ui/icons";
import { makeStyles } from "@material-ui/styles";
import { calcularPorcentaje } from "../utils";

const useStyles = makeStyles((theme) => ({
  metricCard: {
    textAlign: "center",
    padding: theme.spacing(2),
    height: "100%",
  },
  metricNumber: {
    fontSize: 28,
    fontWeight: "bold",
    color: theme.palette.primary.main,
  },
  metricLabel: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
  },
}));

export default function MetricasResumen({
  metricas,
  porCategoria,
  modoMarchaBlanca,
}) {
  const classes = useStyles();

  /**
   * Determina el tipo de banner a mostrar según los resultados
   */
  const getBannerConfig = () => {
    // Modo marcha blanca con perfectos
    if (modoMarchaBlanca && porCategoria.perfectos.cantidad > 0) {
      return {
        type: "marchaBlanca",
        severity: "info",
        icon: <InfoIcon style={{ fontSize: 28 }} />,
        title: "Modo Marcha Blanca Activado",
        message: `${porCategoria.perfectos.cantidad} movimientos con matches perfectos están listos para ser conciliados automáticamente.`,
        actions: [
          "1️⃣ Revisar matches perfectos",
          "2️⃣ Hacer clic en 'Conciliar Perfectos'",
          "3️⃣ Sistema conciliará automáticamente",
        ],
      };
    }

    // Tasa de éxito muy baja
    if (metricas.tasaExito < 50) {
      return {
        type: "bajoRendimiento",
        severity: "warning",
        icon: <WarningIcon style={{ fontSize: 28 }} />,
        title: "Bajo Rendimiento del Motor",
        message: `Solo ${metricas.tasaExito}% de movimientos tienen matches de calidad. El motor necesita ajustes.`,
        actions: [
          "1️⃣ Revisar configuración de tolerancias",
          "2️⃣ Agregar RUTs alternativos",
          "3️⃣ Verificar formato de folios en glosas",
        ],
      };
    }

    // Tasa de éxito excelente
    if (metricas.tasaExito >= 70) {
      return {
        type: "excelente",
        severity: "success",
        icon: <CheckCircleIcon style={{ fontSize: 28 }} />,
        title: "✅ Excelente Procesamiento",
        message: `${metricas.tasaExito}% de los movimientos tienen matches de calidad (≥70 puntos). El motor está funcionando óptimamente.`,
        actions: null,
      };
    }

    // Normal
    return {
      type: "normal",
      severity: "info",
      icon: <InfoIcon />,
      title: "📊 Procesamiento Completado",
      message:
        "Revisa los movimientos sugeridos y procesa manualmente los que no tuvieron match.",
      actions: null,
    };
  };

  const bannerConfig = getBannerConfig();

  return (
    <Box style={{ marginTop: 24 }}>
      {/* ==================== CARDS DE MÉTRICAS ==================== */}
      <Grid container spacing={2} style={{ marginBottom: 24 }}>
        {/* Total Procesados */}
        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.metricCard} elevation={2}>
            <CardContent>
              <Typography className={classes.metricNumber}>
                {metricas.totalProcesados}
              </Typography>
              <Typography className={classes.metricLabel}>
                Movimientos Procesados
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Monto Total */}
        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.metricCard} elevation={2}>
            <CardContent>
              <Typography className={classes.metricNumber}>
                ${(metricas.montoTotal / 1000000).toFixed(2)}M
              </Typography>
              <Typography className={classes.metricLabel}>
                Monto Total
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Score Promedio */}
        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.metricCard} elevation={2}>
            <CardContent>
              <Typography
                className={classes.metricNumber}
                style={{
                  color:
                    metricas.scorePromedio >= 70
                      ? "#4caf50"
                      : metricas.scorePromedio >= 50
                        ? "#ff9800"
                        : "#f44336",
                }}
              >
                {metricas.scorePromedio}
              </Typography>
              <Typography className={classes.metricLabel}>
                Score Promedio
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Tasa de Éxito */}
        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.metricCard} elevation={2}>
            <CardContent>
              <Typography
                className={classes.metricNumber}
                style={{
                  color:
                    metricas.tasaExito >= 70
                      ? "#4caf50"
                      : metricas.tasaExito >= 50
                        ? "#ff9800"
                        : "#f44336",
                }}
              >
                {metricas.tasaExito}%
              </Typography>
              <Typography className={classes.metricLabel}>
                Tasa de Éxito
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* ==================== BARRA DE DISTRIBUCIÓN ==================== */}
      <Box style={{ marginBottom: 24 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          style={{ marginBottom: 8 }}
        >
          <Typography
            variant="body2"
            style={{ fontWeight: 600, color: "#666" }}
          >
            Distribución de Calidad:
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {metricas.tasaExito}% con match ≥70
          </Typography>
        </Box>

        {/* Barra visual */}
        <Box
          style={{
            display: "flex",
            height: 12,
            borderRadius: 6,
            overflow: "hidden",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          }}
        >
          {porCategoria.perfectos.cantidad > 0 && (
            <Tooltip
              title={`🎯 Perfectos: ${
                porCategoria.perfectos.cantidad
              } (${calcularPorcentaje(
                porCategoria.perfectos.cantidad,
                metricas.totalProcesados,
              )}%)`}
              arrow
            >
              <Box
                style={{
                  width: `${calcularPorcentaje(
                    porCategoria.perfectos.cantidad,
                    metricas.totalProcesados,
                  )}%`,
                  backgroundColor: "#4caf50",
                  transition: "all 0.3s ease",
                  cursor: "pointer",
                }}
              />
            </Tooltip>
          )}

          {porCategoria.buenos.cantidad > 0 && (
            <Tooltip
              title={`🟡 Buenos: ${
                porCategoria.buenos.cantidad
              } (${calcularPorcentaje(
                porCategoria.buenos.cantidad,
                metricas.totalProcesados,
              )}%)`}
              arrow
            >
              <Box
                style={{
                  width: `${calcularPorcentaje(
                    porCategoria.buenos.cantidad,
                    metricas.totalProcesados,
                  )}%`,
                  backgroundColor: "#ff9800",
                  transition: "all 0.3s ease",
                  cursor: "pointer",
                }}
              />
            </Tooltip>
          )}

          {porCategoria.debiles.cantidad > 0 && (
            <Tooltip
              title={`🟠 Débiles: ${
                porCategoria.debiles.cantidad
              } (${calcularPorcentaje(
                porCategoria.debiles.cantidad,
                metricas.totalProcesados,
              )}%)`}
              arrow
            >
              <Box
                style={{
                  width: `${calcularPorcentaje(
                    porCategoria.debiles.cantidad,
                    metricas.totalProcesados,
                  )}%`,
                  backgroundColor: "#ffeb3b",
                  transition: "all 0.3s ease",
                  cursor: "pointer",
                }}
              />
            </Tooltip>
          )}

          {porCategoria.sinMatch.cantidad > 0 && (
            <Tooltip
              title={`🔴 Sin Match: ${
                porCategoria.sinMatch.cantidad
              } (${calcularPorcentaje(
                porCategoria.sinMatch.cantidad,
                metricas.totalProcesados,
              )}%)`}
              arrow
            >
              <Box
                style={{
                  width: `${calcularPorcentaje(
                    porCategoria.sinMatch.cantidad,
                    metricas.totalProcesados,
                  )}%`,
                  backgroundColor: "#f44336",
                  transition: "all 0.3s ease",
                  cursor: "pointer",
                }}
              />
            </Tooltip>
          )}

          {porCategoria.errores.cantidad > 0 && (
            <Tooltip
              title={`⚪ Errores: ${
                porCategoria.errores.cantidad
              } (${calcularPorcentaje(
                porCategoria.errores.cantidad,
                metricas.totalProcesados,
              )}%)`}
              arrow
            >
              <Box
                style={{
                  width: `${calcularPorcentaje(
                    porCategoria.errores.cantidad,
                    metricas.totalProcesados,
                  )}%`,
                  backgroundColor: "#9e9e9e",
                  transition: "all 0.3s ease",
                  cursor: "pointer",
                }}
              />
            </Tooltip>
          )}
        </Box>

        {/* Leyenda de colores */}
        <Box
          display="flex"
          justifyContent="space-around"
          flexWrap="wrap"
          style={{ marginTop: 12, gap: 8 }}
        >
          {porCategoria.perfectos.cantidad > 0 && (
            <Box display="flex" alignItems="center" gap={0.5}>
              <Box
                style={{
                  width: 12,
                  height: 12,
                  backgroundColor: "#4caf50",
                  borderRadius: 2,
                }}
              />
              <Typography variant="caption">
                Perfectos ({porCategoria.perfectos.cantidad})
              </Typography>
            </Box>
          )}

          {porCategoria.buenos.cantidad > 0 && (
            <Box display="flex" alignItems="center" gap={0.5}>
              <Box
                style={{
                  width: 12,
                  height: 12,
                  backgroundColor: "#ff9800",
                  borderRadius: 2,
                }}
              />
              <Typography variant="caption">
                Buenos ({porCategoria.buenos.cantidad})
              </Typography>
            </Box>
          )}

          {porCategoria.debiles.cantidad > 0 && (
            <Box display="flex" alignItems="center" gap={0.5}>
              <Box
                style={{
                  width: 12,
                  height: 12,
                  backgroundColor: "#ffeb3b",
                  borderRadius: 2,
                }}
              />
              <Typography variant="caption">
                Débiles ({porCategoria.debiles.cantidad})
              </Typography>
            </Box>
          )}

          {porCategoria.sinMatch.cantidad > 0 && (
            <Box display="flex" alignItems="center" gap={0.5}>
              <Box
                style={{
                  width: 12,
                  height: 12,
                  backgroundColor: "#f44336",
                  borderRadius: 2,
                }}
              />
              <Typography variant="caption">
                Sin Match ({porCategoria.sinMatch.cantidad})
              </Typography>
            </Box>
          )}

          {porCategoria.errores.cantidad > 0 && (
            <Box display="flex" alignItems="center" gap={0.5}>
              <Box
                style={{
                  width: 12,
                  height: 12,
                  backgroundColor: "#9e9e9e",
                  borderRadius: 2,
                }}
              />
              <Typography variant="caption">
                Errores ({porCategoria.errores.cantidad})
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* ==================== BANNER CONTEXTUAL ==================== */}
      <Box style={{ marginBottom: 24, width: "100%" }}>
        {bannerConfig.type === "marchaBlanca" ? (
          <CardContent
            style={{
              backgroundColor: "#e3f2fd",
              border: "2px solid #2196f3",
              borderRadius: 8,
            }}
          >
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={8}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  {bannerConfig.icon}
                  <Typography
                    variant="body1"
                    style={{ fontWeight: 600, color: "#1565c0" }}
                  >
                    {bannerConfig.title}
                  </Typography>
                </Box>
                <Typography variant="body2" style={{ color: "#1565c0" }}>
                  {bannerConfig.message}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box
                  style={{
                    backgroundColor: "rgba(33, 150, 243, 0.1)",
                    padding: 12,
                    borderRadius: 8,
                  }}
                >
                  <Typography
                    variant="caption"
                    style={{
                      fontWeight: 600,
                      color: "#1565c0",
                      display: "block",
                      marginBottom: 8,
                    }}
                  >
                    📋 Pasos:
                  </Typography>
                  <Box display="flex" flexDirection="column" gap={1}>
                    {bannerConfig.actions.map((action, idx) => (
                      <Typography
                        key={idx}
                        variant="caption"
                        style={{ color: "rgba(21, 101, 192, 0.85)" }}
                      >
                        {action}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        ) : bannerConfig.type === "bajoRendimiento" ? (
          <CardContent
            style={{
              backgroundColor: "#fff3e0",
              border: "2px solid #ff9800",
              borderRadius: 8,
            }}
          >
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={8}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  {bannerConfig.icon}
                  <Typography
                    variant="body1"
                    style={{ fontWeight: 600, color: "#e65100" }}
                  >
                    {bannerConfig.title}
                  </Typography>
                </Box>
                <Typography variant="body2" style={{ color: "#e65100" }}>
                  {bannerConfig.message}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box
                  style={{
                    backgroundColor: "rgba(255, 152, 0, 0.1)",
                    padding: 12,
                    borderRadius: 8,
                  }}
                >
                  <Typography
                    variant="caption"
                    style={{
                      fontWeight: 600,
                      color: "#e65100",
                      display: "block",
                      marginBottom: 8,
                    }}
                  >
                    💡 Acciones Recomendadas:
                  </Typography>
                  <Box display="flex" flexDirection="column" gap={1}>
                    {bannerConfig.actions.map((action, idx) => (
                      <Typography
                        key={idx}
                        variant="caption"
                        style={{ color: "rgba(230, 81, 0, 0.85)" }}
                      >
                        {action}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        ) : (
          <Alert
            severity={bannerConfig.severity}
            icon={bannerConfig.icon}
            style={{
              borderRadius: 8,
              border: `2px solid ${
                bannerConfig.severity === "success" ? "#4caf50" : "#2196f3"
              }`,
            }}
          >
            <Typography
              variant="body2"
              style={{
                fontWeight: 600,
                marginBottom: 4,
                color:
                  bannerConfig.severity === "success" ? "#2e7d32" : "#1565c0",
              }}
            >
              {bannerConfig.title}
            </Typography>
            <Typography
              variant="body2"
              style={{
                color:
                  bannerConfig.severity === "success" ? "#2e7d32" : "#1565c0",
              }}
            >
              {bannerConfig.message}
            </Typography>
          </Alert>
        )}
      </Box>
    </Box>
  );
}
